import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { SingularEmailBaseCampaign } from '@feathr/blackbox';
import { isMailrailCampaign } from '@feathr/blackbox';
import { Tab } from '@feathr/components';
import ReportActions from '@feathr/extender/components/PageHeaderActions/ReportActions';
import { useReportExport } from '@feathr/extender/hooks';
import { useFlags } from '@feathr/extender/state';

import EmailDetails from '../../../../components/EmailDetails';
import MailrailEmailDetails from '../../../../components/MailrailEmailDetails';
import CampaignPage from '../../CampaignPage';
import CampaignReportSection, { getCampaignReportConfig } from '../CampaignReportSection';
import ABTestReport, { isABTestReportVisible } from './ABTestReport';

import * as styles from './PinpointEmailCampaignReportPage.css';

type TReportTab = 'abTest' | 'report';

interface IProps {
  campaign: SingularEmailBaseCampaign;
}

function PinpointEmailCampaignReportPage({ campaign }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const flags = useFlags();
  const [activeTab, setActiveTab] = useState<TReportTab>('report');
  const emailCampaignDetailSection = isMailrailCampaign(campaign) ? (
    <MailrailEmailDetails campaign={campaign} />
  ) : (
    <EmailDetails campaign={campaign} />
  );
  const config = getCampaignReportConfig(campaign);
  const reportExportProps = useReportExport({ model: campaign, config });
  const actions = <ReportActions campaign={campaign} {...reportExportProps} />;
  const isABTestReport = isABTestReportVisible(flags, campaign);

  function handleClickReportTab(): void {
    setActiveTab('report');
  }

  function handleClickABTestTab(): void {
    setActiveTab('abTest');
  }

  const tabs = isABTestReport
    ? [
        <Tab
          active={activeTab === 'report'}
          key={'report'}
          onClick={handleClickReportTab}
          title={t('Report')}
        />,
        <Tab
          active={activeTab === 'abTest'}
          key={'abTest'}
          onClick={handleClickABTestTab}
          title={t('A/B test')}
        />,
      ]
    : undefined;

  return (
    <CampaignPage actions={actions} campaign={campaign} tabs={tabs} title={campaign.name}>
      {isABTestReport && activeTab === 'abTest' ? (
        <ABTestReport campaign={campaign} />
      ) : (
        <>
          {emailCampaignDetailSection}
          <h3 className={styles.title}>{t('Report')}</h3>
          <CampaignReportSection campaign={campaign} {...reportExportProps} />
        </>
      )}
    </CampaignPage>
  );
}

export default observer(PinpointEmailCampaignReportPage);
